import type { ComponentSchema, FieldSchema } from '../../../../../schema-types'
import { outlineButtonFields } from '../shared/button-schema-fields'

/**
 * Home sections Neon Cinema renders with its own markup (sections/<type>/themes/lightings-1.vue), described by
 * the fields those overrides read. Colour fields the overrides ignore (bgColor, textColor) are left out: the
 * theme keeps its tokens so text holds its contrast.
 */

const HOME_PAGES = ['home', 'shop', 'category', 'categories']

const productFields = (key: string): FieldSchema[] => outlineButtonFields(`buttons.${key}`, 'Add to Cart Button')

export const announcementBarSchema: ComponentSchema = {
  type: 'announcement-bar',
  label: 'Announcement Ticker',
  description: 'A ticker of short messages; a coupon code shows as an amber chip after its message.',
  supportedPages: ['home', 'shop', 'product', 'category', 'categories', 'cart', 'checkout', 'contact', 'wishlist'],
  variants: [{
    id: 'v1',
    label: 'Ticker',
    fields: [{
      key: 'items',
      label: 'Messages',
      type: 'array',
      itemSchema: [
        { key: 'text', label: 'Message', type: 'string' },
        { key: 'code', label: 'Coupon code (optional)', type: 'string' },
      ],
    }],
  }],
  commonFields: [],
}

export const flashSaleSchema: ComponentSchema = {
  type: 'flash-sale',
  label: 'Flash Sale Ticker',
  description: 'A ticker strip with a departure-board countdown over a rail of the most discounted products. Hidden after it ends.',
  supportedPages: ['home'],
  variants: [{
    id: 'v1',
    label: 'Countdown ticker',
    fields: [
      { key: 'title', label: 'Title', type: 'string', default: 'Flash sale' },
      { key: 'endDate', label: 'Ends at (ISO date)', type: 'string', placeholder: '2026-12-31T23:59:59' },
    ],
  }],
  commonFields: [],
}

const SLIDER_HEADING: FieldSchema[] = [
  { key: 'title', label: 'Title', type: 'string', default: 'Featured products' },
  { key: 'subtitle', label: 'Eyebrow', type: 'string' },
]

export const productSliderSchema: ComponentSchema = {
  type: 'product-slider',
  label: 'Product Slider',
  description: 'A rail of product cards, optionally with category tabs.',
  supportedPages: HOME_PAGES,
  variants: [
    {
      id: 'v1',
      label: 'V1 — Category tabs',
      fields: [
        ...SLIDER_HEADING,
        {
          key: 'tabs',
          label: 'Tabs',
          type: 'array',
          itemSchema: [
            { key: 'label', label: 'Tab label', type: 'string' },
            { key: 'categoryId', label: 'Category', type: 'category-select' },
          ],
        },
      ],
    },
    { id: 'v2', label: 'V2 — Single rail, centred heading', fields: SLIDER_HEADING },
    { id: 'v3', label: 'V3 — Single rail', fields: SLIDER_HEADING },
  ],
  commonFields: productFields('addToCart'),
}

const GRID_FIELDS: FieldSchema[] = [
  { key: 'title', label: 'Title', type: 'string', default: 'All products' },
  { key: 'subtitle', label: 'Eyebrow', type: 'string' },
  { key: 'viewAllLink', label: 'View all link', type: 'string', default: '/shop' },
]

export const productGridSchema: ComponentSchema = {
  type: 'product-grid',
  label: 'Product Grid',
  description: 'Product cards as a grid, a spotlight (one large product beside smaller ones) or spec rows.',
  supportedPages: HOME_PAGES,
  variants: [
    { id: 'v1', label: 'V1 — Grid', fields: GRID_FIELDS },
    { id: 'v2', label: 'V2 — Spotlight, 3 columns', fields: GRID_FIELDS },
    { id: 'v3', label: 'V3 — Spec rows', fields: GRID_FIELDS },
    { id: 'v4', label: 'V4 — Spotlight, 2 columns', fields: GRID_FIELDS },
    { id: 'v5', label: 'V5 — Grid', fields: GRID_FIELDS },
    {
      id: 'v6',
      label: 'V6 — Spec rows with special offer',
      fields: [...GRID_FIELDS, { key: 'offerEndsAt', label: 'Offer ends at (ISO date)', type: 'string' }],
    },
  ],
  commonFields: [
    { key: 'categoryId', label: 'Category (optional)', type: 'category-select' },
    {
      key: 'orderBy',
      label: 'Order',
      type: 'select',
      options: [
        { label: 'Newest', value: 'newest_first' },
        { label: 'Biggest discount', value: 'discount_desc' },
        { label: 'Price: low to high', value: 'price_asc' },
        { label: 'Price: high to low', value: 'price_desc' },
        { label: 'Name: A–Z', value: 'name_asc' },
      ],
      default: 'newest_first',
    },
    { key: 'limit', label: 'Products', type: 'number', default: 8 },
    ...productFields('addToCart'),
  ],
}

const BANNER_FIELDS: FieldSchema[] = [
  { key: 'image', label: 'Image', type: 'image' },
  { key: 'title', label: 'Title', type: 'string' },
  { key: 'subtitle', label: 'Subtitle', type: 'text' },
  { key: 'ctaText', label: 'Button text', type: 'string', default: 'Shop now' },
  { key: 'ctaLink', label: 'Button link', type: 'string', default: '/shop' },
]

export const lookbookBannerSchema: ComponentSchema = {
  type: 'lookbook-banner',
  label: 'Lookbook Banner',
  description: 'A glass panel with a lit picture beside the text (V1), or the letterboxed "cinema" band (V2).',
  supportedPages: ['home', 'shop', 'category'],
  variants: [
    {
      id: 'v1',
      label: 'V1 — Lit frame and text',
      fields: [
        ...BANNER_FIELDS,
        {
          key: 'imagePosition',
          label: 'Picture side',
          type: 'select',
          options: [{ label: 'Left', value: 'left' }, { label: 'Right', value: 'right' }],
          default: 'left',
        },
      ],
    },
    {
      id: 'v2',
      label: 'V2 — Cinema band',
      fields: [...BANNER_FIELDS, { key: 'overlayOpacity', label: 'Dark wash (40–100)', type: 'number', default: 55 }],
    },
  ],
  commonFields: [],
}

const REVIEW_ITEM: FieldSchema[] = [
  { key: 'avatar', label: 'Photo', type: 'image' },
  { key: 'name', label: 'Name', type: 'string' },
  { key: 'designation', label: 'Detail (city, product…)', type: 'string' },
  { key: 'review', label: 'Review', type: 'text' },
  { key: 'rating', label: 'Rating (1–5)', type: 'number', default: 5 },
]
const REVIEW_FIELDS: FieldSchema[] = [
  { key: 'title', label: 'Title', type: 'string', default: 'What customers say' },
  { key: 'items', label: 'Reviews', type: 'array', itemSchema: REVIEW_ITEM },
]

export const testimonialsSchema: ComponentSchema = {
  type: 'testimonials',
  label: 'Testimonials',
  description: 'Customer quotes: one at a time in the spotlight (V1) or a grid of glass cards (V2).',
  supportedPages: ['home', 'shop'],
  variants: [
    { id: 'v1', label: 'V1 — Spotlight quote', fields: REVIEW_FIELDS },
    { id: 'v2', label: 'V2 — Card grid', fields: REVIEW_FIELDS },
  ],
  commonFields: [],
}

export const brandsSchema: ComponentSchema = {
  type: 'brands',
  label: 'Brands',
  description: 'Brand logos on glass tiles or a slow ticker; empty uses the store\'s brands.',
  supportedPages: ['home', 'shop', 'categories'],
  variants: [{
    id: 'v1',
    label: 'Logos',
    fields: [
      { key: 'title', label: 'Title', type: 'string' },
      {
        key: 'layout',
        label: 'Layout',
        type: 'select',
        options: [{ label: 'Grid', value: 'grid' }, { label: 'Ticker', value: 'ticker' }],
        default: 'grid',
      },
      {
        key: 'items',
        label: 'Brands',
        type: 'array',
        itemSchema: [
          { key: 'logo', label: 'Logo', type: 'image' },
          { key: 'name', label: 'Name', type: 'string' },
          { key: 'link', label: 'Link', type: 'string' },
        ],
      },
    ],
  }],
  commonFields: [],
}

const POST_ITEM: FieldSchema[] = [
  { key: 'title', label: 'Title', type: 'string' },
  { key: 'image', label: 'Cover', type: 'image' },
  { key: 'excerpt', label: 'Excerpt', type: 'text' },
  { key: 'slug', label: 'Slug', type: 'string' },
  { key: 'date', label: 'Date', type: 'string' },
  { key: 'category', label: 'Topic', type: 'string' },
]
const BLOG_SOURCE_FIELD: FieldSchema = { key: 'source', label: 'Posts to show', type: 'select', options: [{ label: 'Live posts from the blog', value: 'posts' }, { label: 'Posts typed here', value: 'manual' }], help: 'Without a choice: typed posts when there are some, else the live blog.' }
const BLOG_FIELDS: FieldSchema[] = [
  { key: 'title', label: 'Title', type: 'string', default: 'From the blog' },
  { key: 'posts', label: 'Posts', type: 'array', itemSchema: POST_ITEM },
  { key: 'viewAllLink', label: 'View all link', type: 'string', default: '/blog' },
  { key: 'showViewAll', label: 'Show "View all"', type: 'boolean', default: true },
]

export const blogSchema: ComponentSchema = {
  type: 'blog',
  label: 'Blog',
  description: 'Post cards (V1, V3) or compact rows (V2); on /blog/<slug> the matching post as an article.',
  supportedPages: ['home', 'blog', 'shop'],
  variants: [
    { id: 'v1', label: 'V1 — Cards', fields: BLOG_FIELDS },
    { id: 'v2', label: 'V2 — Rows', fields: BLOG_FIELDS },
    { id: 'v3', label: 'V3 — Cards', fields: BLOG_FIELDS },
  ],
  commonFields: [BLOG_SOURCE_FIELD],
}

export const ctaSchema: ComponentSchema = {
  type: 'cta',
  label: 'Newsletter',
  description: 'A glass band with the title and an email field; the form shows only with an external form URL.',
  supportedPages: ['home', 'shop', 'categories'],
  variants: [{
    id: 'v1',
    label: 'Newsletter band',
    fields: [
      { key: 'title', label: 'Title', type: 'string', default: 'Get first light on new drops' },
      { key: 'subtitle', label: 'Subtitle', type: 'string' },
      { key: 'placeholder', label: 'Email placeholder', type: 'string', default: 'Your email address' },
      { key: 'buttonText', label: 'Button text', type: 'string', default: 'Subscribe' },
      { key: 'formAction', label: 'Form URL', type: 'string', help: 'An external newsletter form; without it the band shows no form.' },
    ],
  }],
  commonFields: [],
}

export const wishlistSchema: ComponentSchema = {
  type: 'wishlist',
  label: 'Wishlist',
  description: 'The products this shopper saved.',
  supportedPages: ['wishlist'],
  variants: [{ id: 'v1', label: 'Saved products' }],
  commonFields: [
    { key: 'title', label: 'Title', type: 'string', default: 'Wishlist' },
    { key: 'subtitle', label: 'Subtitle', type: 'string' },
    { key: 'emptyTitle', label: 'Empty title', type: 'string' },
    { key: 'emptyText', label: 'Empty message', type: 'string' },
    { key: 'continueShoppingText', label: 'Continue shopping text', type: 'string' },
    { key: 'continueShoppingLink', label: 'Continue shopping link', type: 'string', default: '/shop' },
  ],
}

export const compareSchema: ComponentSchema = {
  type: 'compare',
  label: 'Compare',
  description: 'Up to four products side by side with a spec table built from their attributes.',
  supportedPages: ['compare'],
  variants: [{ id: 'v1', label: 'Spec table' }],
  commonFields: [{ key: 'title', label: 'Title', type: 'string', default: 'Compare' }],
}

export const couponSchema: ComponentSchema = {
  type: 'coupon',
  label: 'Coupons',
  description: 'Coupon codes on ticket stubs with copy-to-clipboard.',
  supportedPages: ['coupon'],
  variants: [{ id: 'v1', label: 'Ticket stubs' }],
  commonFields: [
    { key: 'title', label: 'Title', type: 'string', default: 'Coupons' },
    {
      key: 'coupons',
      label: 'Coupons',
      type: 'array',
      itemSchema: [
        { key: 'code', label: 'Code', type: 'string' },
        { key: 'title', label: 'Title', type: 'string' },
        { key: 'description', label: 'Description', type: 'string' },
        { key: 'discount', label: 'Discount label', type: 'string', placeholder: '10% off' },
        { key: 'expiryDate', label: 'Expires (ISO date)', type: 'string' },
      ],
    },
  ],
}
